<template>
  <ListItem
    :link="routeConfig"
    :images="images"
    class="spa-card"
    lateral-images-desktop
    is-external
  >
    <template #item-tags>
      <div>
        <Badge
          v-if="spa.score > 0"
          class="score-badge"
        >
          <ScoreLeafIcon :size="20" />
          <span>{{ spa.score.toFixed(1) }}</span>
        </Badge>
      </div>
    </template>

    <template #body>
      <div class="spa-card__content">
        <div class="spa-card__title">
          <h2 class="spa-card__title__spa-name">
            {{ spa.name }}
          </h2>
        </div>

        <HotelNameStarsAtom
          v-if="spa.hotel && spa.hotel.name"
          :hotel="spa.hotel"
        />

        <LocationLink
          :location="spa.location"
          :show-icon="true"
          class="spa-card__location-link"
          :go-to-map-enabled="false"
        />
        <span
          v-if="giftVoucherLabel"
          class="spa-card__voucher-gift"
        >{{ giftVoucherLabel }} </span>

        <Badge
          v-if="spa.status !== 'online'"
          color="acent"
          class="spa-card__badge"
        >
          <SpaStatusLabel :spa-status="spa.status" />
        </Badge>

        <div
          v-if="services.length"
          class="spa-card__promo-tags-services"
        >
          <p class="spa-card__promo-tags-services__header">
            <span>{{ filteredByPromoServicesHeaderLabel }}</span>
          </p>

          <div class="spa-card__promo-tags-services__wrapper">
            <div class="spa-card__promo-tags-services__item">
              <template
                v-for="(promo) in promoTagListOrServicesByPromosOrDefaultServicesTrucated"
                :key="promo.tag.uuid + '-mobile'"
              >
                <div class="spa-card__promo-tags-services__item__title">
                  <Lang
                    :by-raw="promo.tag.texts"
                    key-for-raw="title"
                  />
                </div>
                <div class="spa-card__promo-tags-services__item__price">
                  <PersonIcon
                    v-if="promo.isIndividual"
                    :size="16"
                  />
                  <span
                    v-if="promo.isIndividual"
                    class="price--number"
                  >
                    {{ promo.truncatedPrice }}
                  </span>
                </div>
                <div class="spa-card__promo-tags-services__item__couple-price">
                  <PersonCoupleIcon :size="16" />
                  <span class="price--number">{{ promo.truncatedCouplePrice }}</span>
                </div>
              </template>
            </div>
            <div>
              <SplButton variant="vertical">
                <ArrowIcon
                  direction="right"
                  variant="dark"
                />
              </SplButton>
            </div>
          </div>
        </div>
      </div>
    </template>
  </ListItem>
</template>

<script lang="ts">
import SpaStatusLabel from './SpaStatusLabel.vue'
import ListItem from '../shared/ListItem.vue'
import LocationLink from '../shared/LocationLink.vue'
import HotelNameStarsAtom from '../hotel/atom/HotelNameStars.atom.vue'
import Badge from '../shared/Badge.vue'
import ArrowIcon from '../shared/icons/Arrow.icon.vue'
import ScoreLeafIcon from '../shared/icons/ScoreLeaf.icon.vue'
import PersonIcon from '../shared/icons/Person.icon.vue'
import PersonCoupleIcon from '../shared/icons/PersonCouple.icon.vue'
import SplButton from '../shared/form/SplButton.vue'

import { SpaMiniList } from '~/core/ts/entity/Spa'
import { Money } from '~/core/ts/entity/Money'

import { type Tag } from '~/core/ts/entity/Tag'
import { Promotion } from '~/core/ts/entity/Promotion'
import { CheckBoxNode } from '~/core/ts/entity/CheckBoxNode'

import { capitalizeFirstWord } from '~/core/ts/util/string'
import { translate, translateByKey } from '~/core/ts/util/translate'

import { Service, ServiceCollection, ServiceMiniListCollection } from '~/core/ts/entity/Service'

type TagOrServiceDisplay = {
  tag: Tag,
  isIndividual: boolean,
  price: string,
  couplePrice: string,
  truncatedPrice: string,
  truncatedCouplePrice: string,
}

export default defineNuxtComponent({
  components: {
    SpaStatusLabel,
    ListItem,
    LocationLink,
    HotelNameStarsAtom,
    Badge,
    ArrowIcon,
    ScoreLeafIcon,
    PersonIcon,
    PersonCoupleIcon,
    SplButton,
  },
  props: {
    spa: {
      type: Object as PropType<SpaMiniList>,
      required: true,
    },
    selectedPromos: {
      type: Array as PropType<Array<CheckBoxNode>>,
      default: () => [],
    },
  },
  setup(props) {
    const images = props.spa.images
    const description = translate(props.spa.texts).shortDescription
    const services = props.spa.services

    const routeConfig = { path: props.spa.uri }
    const giftVoucherLabel = computed(() => {
      if (!props.spa.giftVoucherAvailable) {
        return ''
      }
      return capitalizeFirstWord(translateByKey('is-gift-voucher-available'))
    })

    const promoTagListOrServicesByPromos = computed<Array<TagOrServiceDisplay>>(() => {
      // Si tenemos una categoría/promo seleccionada, mostramos los servicios relevantes.
      if (props.selectedPromos.length) {
        const collection = new ServiceMiniListCollection(services)
        collection.filterByPromotionIDs(props.selectedPromos.map((promo) => promo.id))

        return collection.services
          .sort((serviceA, serviceB) => Money.compareWith(serviceA.pricePerPerson, serviceB.pricePerPerson))
          .map((service): TagOrServiceDisplay => {
            const uuid = service.uuid
            const title = translate(service.texts).name

            const texts: TagOrServiceDisplay['tag']['texts'] = {}

            Object.entries(service.texts).forEach(([key, value]) => {
              texts[key] = {
                title: value.name,
                description: value.description,
                slug: value.slug,
              }
            })

            return {
              tag: {
                uuid,
                uuidParents: [],
                title,
                description: '',
                slug: '',
                texts,
              },
              isIndividual: Service.isIndividual(service),
              price: Money.toString(service.price),
              couplePrice: Money.toString(Money.multiplyBy(service.pricePerPerson, 2)),
              truncatedPrice: Money.toString(service.price, 0),
              truncatedCouplePrice: Money.toString(Money.multiplyBy(service.pricePerPerson, 2), 0),
            }
          })
      }

      // Sin categoría, mostramos las promos de este Spa.
      return props.spa.promoTags
        .filter((promotion) => !Promotion.isOther(promotion.tag))
        .sort((promotionTag1, promotionTag2) => Money.compareWith(promotionTag1.price, promotionTag2.price))
        .map((promotion) => {
          return {
            tag: promotion.tag,
            isIndividual: !Promotion.isCouple(promotion.tag),
            price: Money.toString(promotion.price),
            couplePrice: Money.toString(Money.multiplyBy(promotion.price, 2)),

            truncatedPrice: Money.toString(promotion.price, 0),
            truncatedCouplePrice: Money.toString(Money.multiplyBy(promotion.price, 2), 0),
          }
        })
    })

    const promoTagListOrServicesByPromosOrDefaultServicesTrucated = computed<Array<TagOrServiceDisplay>>(() => {
      // Mostramos 4 servicios en caso de que no haya servicios con promociones.
      if (promoTagListOrServicesByPromos.value.length === 0) {
        const collection = new ServiceCollection(services)
        collection.sortByCheaper(new Date())

        return collection.services
          .slice(0, 4)
          .map((service): TagOrServiceDisplay => {
            const uuid = service.uuid
            const title = translate(service.texts).name

            const texts: TagOrServiceDisplay['tag']['texts'] = {}

            Object.entries(service.texts).forEach(([key, value]) => {
              texts[key] = {
                title: value.name,
                description: value.description,
                slug: value.slug,
              }
            })

            return {
              tag: {
                uuid,
                uuidParents: [],
                title,
                description: '',
                slug: '',
                texts,
              },
              isIndividual: Service.isIndividual(service),
              price: Money.toString(service.price),
              couplePrice: Money.toString(Money.multiplyBy(service.pricePerPerson, 2)),

              truncatedPrice: Money.toString(service.price, 0),
              truncatedCouplePrice: Money.toString(Money.multiplyBy(service.pricePerPerson, 2), 0),
            }
          })
      }

      return promoTagListOrServicesByPromos.value.slice(0, 4)
    })

    const filteredByPromoServicesHeaderLabel = computed(() => {
      const count = promoTagListOrServicesByPromos.value.length
      if (count == 0) {
        const availableServicesLabel = translateByKey('available-service', true)
        return `${services.length} ${availableServicesLabel}:`
      }

      if (props.selectedPromos.length == 0) {
        const availableServicesLabel = translateByKey('available-service-in-category', services.length > 1)
        return `${services.length} ${availableServicesLabel}:`
      }

      const label = `${count} ${translateByKey('service-for', count > 1)} `

      const labels = props.selectedPromos.map((categoryNode) => categoryNode.label)
      const promoNamesStr = labels.join(', ')

      return label + promoNamesStr
    })

    return {
      images,
      description,
      services,
      routeConfig,
      giftVoucherLabel,

      promoTagListOrServicesByPromos,
      promoTagListOrServicesByPromosOrDefaultServicesTrucated,
      filteredByPromoServicesHeaderLabel,
    }
  },
})
</script>

<style lang="scss" scoped>
.score-badge {
  @apply flex flex-row;
  @apply font-bold;
  @apply justify-center items-center;
}
.spa-card {
  @apply flex flex-col;
  @apply relative;
  @apply w-full;

  &__badge {
    @apply absolute;
    @apply top-25p left-20p;

    @screen lg {
      @apply top-35p left-35p;
    }
  }

  &__voucher-gift {
    @apply hidden;

    @screen lg {
      @apply block;
      @apply text-base text-spl-dark;
    }
  }

  @screen lg {
    @apply w-full;
    @apply flex-row;
    @apply p-0;
  }

  &__content {
    @apply w-full;
    @apply px-1;

    @screen lg {
      min-height: 250px;
    }
  }

  &__title {
    @apply mb-1;

    &__spa-name {
      @apply font-bold text-xl;
      @apply text-spl-dark;
    }

    &__score {
      @apply hidden;

      @screen lg {
        @apply flex;
      }
    }

    @screen lg {
      @apply mt-2;

      &__spa-name {
        @apply text-2xl;
      }
    }
  }

  &__promo-tags-services {
    @apply border-t-1;
    @apply py-2;

    &__header {
      @apply text-spl-dark;
    }

    &__wrapper {
      @apply pt-2;
      @apply flex justify-between content-start items-start;

      button {
        width: 16px;
        min-height: 120px;

        @apply h-full;
        @apply ml-2;

        @screen lg {
          @apply hidden;
        }
      }

      @screen lg {
        @apply block;
      }
    }

    &__item {
      @apply w-full;

      @apply grid;
      grid-template-columns: 54% 23% 23%;

      @screen md {
        grid-template-columns: 70% 15% 15%;
      }

      @screen lg {
        grid-template-columns: 60% 20% 20%;
      }

      @apply text-spl-dark;

      &__title {
        @apply border-b-1;
        @apply py-1;

        @apply whitespace-nowrap;
        @apply overflow-hidden;
        text-overflow: ellipsis;

        @screen md {
          @apply px-1;
        }
      }

      &__price, &__couple-price {
        @apply flex items-center justify-center;
        @apply font-semibold;

        @apply border-b-1;

        @apply py-1 pr-1;
        @screen md {
          @apply px-1;
        }

        .price-tag {
          @apply text-left;
          @apply pl-1;
          min-width: 32px;
        }
      }

      &__price {
        @apply border-r-1;
      }
    }

    .price--number {
      @apply text-left;
      @apply pl-1;
      min-width: 32px;
    }
  }

  &__location-link {
    @apply mb-2;
  }

  &__description {
    @apply opacity-0;
    @apply h-0;
    white-space: pre-wrap;
    transition: height ease-in-out 300ms, padding ease-in-out 300ms, opacity ease-in-out 300ms;

    @screen lg {
      @apply mx-0;
    }

    &--expanded {
      @screen lg {
        @apply opacity-100;
        height: inherit;
        @apply py-1;
      }
    }
  }

  &__toggle-description-button {
    @apply hidden;

    @screen lg {
      @apply flex;
      @apply justify-center;
      @apply w-full;

      @apply p-2 mt-auto;
    }

    z-index: 1;
  }
}
</style>
